import { characterStyles, type CharacterStyle } from '../core/characters';
import type { BotDifficulty } from '../sim/rules/settings';
import { MAX_PLAYERS } from '../core/players';
import { CONTROLLER_GLYPH_OPTIONS, type ControllerGlyphs } from '../input/presentation';
import { partyOptions, type PartyOptions } from './partyOptions';
import { aimPreferences, DEFAULT_AIM_PREFERENCES, type AimPreferences } from '../input/aim';

export type UserSettings = PartyOptions & AimPreferences & {
  maxHp: number;
  firstTo: number;
  botDifficulty: BotDifficulty;
  quickBots: number;
  favoriteArenas: string[];
  roundTimeLimit: number;
  showWins: boolean;
  rotation: 'random' | 'ordered';
  enabledWeapons: string[] | 'all';
  enabledLevels: string[] | 'all';
  haptics: boolean;
  colorblind: boolean;
  reduceShake: boolean;
  reduceBlood: boolean;
  renderer: 'auto' | 'gpu' | 'canvas';
  sfx: number;
  music: number;
  lighting: boolean;
  includeUserLevels: boolean;
  /** Override labels when the browser hides the controller's identity. Bindings stay unchanged. */
  controllerGlyphs: ControllerGlyphs;
  /** Send anonymous usage and frame-pacing statistics (src/telemetry). */
  telemetry: boolean;
  showReplays: boolean;
  characters: CharacterStyle[];
};

const KEY = 'floppy-clash.settings';

export const DEFAULT_USER_SETTINGS: UserSettings = {
  ...partyOptions(),
  ...DEFAULT_AIM_PREFERENCES,
  maxHp: 100,
  firstTo: 5,
  botDifficulty: 'standard',
  quickBots: 3,
  favoriteArenas: [],
  roundTimeLimit: 60,
  showWins: true,
  rotation: 'random',
  enabledWeapons: 'all',
  enabledLevels: 'all',
  haptics: true,
  colorblind: false,
  reduceShake: false,
  reduceBlood: false,
  renderer: 'auto',
  sfx: 0.8,
  music: 0.25,
  lighting: false,
  includeUserLevels: true,
  controllerGlyphs: 'auto',
  telemetry: true,
  showReplays: true,
  characters: characterStyles(),
};

export function loadSettings(): UserSettings {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<UserSettings>;
    const settings = { ...DEFAULT_USER_SETTINGS, ...saved, ...partyOptions(saved), ...aimPreferences(saved) };
    settings.favoriteArenas = Array.isArray(saved.favoriteArenas) ? [...new Set(saved.favoriteArenas.filter((id) => typeof id === 'string' && id.length <= 128))].slice(0, 2048) : [];
    settings.characters = characterStyles(saved.characters);
    settings.quickBots = Number.isFinite(settings.quickBots) ? Math.max(1, Math.min(MAX_PLAYERS - 1, Math.round(settings.quickBots))) : 3;
    if (!CONTROLLER_GLYPH_OPTIONS.includes(settings.controllerGlyphs))
      settings.controllerGlyphs = 'auto';
    if (typeof settings.showReplays !== 'boolean') settings.showReplays = true;
    return settings;
  } catch {
    return { ...DEFAULT_USER_SETTINGS, favoriteArenas: [], characters: characterStyles() };
  }
}

export function saveSettings(s: UserSettings): void {
  localStorage.setItem(KEY, JSON.stringify(s));
}
